import React, { useState } from 'react';
import { 
  CheckCircle, 
  Clock, 
  User, 
  Crown, 
  Shield, 
  AlertTriangle,
  Play,
  MessageSquare,
  Paperclip,
  ArrowRight,
  Info,
  BookOpen,
  Zap,
  Timer,
  Calendar,
  Target,
  TrendingUp,
  Gauge,
  Users,
  Edit,
  Search,
  Database,
  Brain,
  Lightbulb,
  Shield as ShieldIcon,
  Activity,
  AlertCircle,
  Github,
  Download,
  Smartphone,
  Key,
  LogIn,
  Settings,
  Lock,
  FileText,
  GitBranch,
  Rocket,
  StickyNote,
  Globe,
  EyeOff,
  Minimize2,
  FolderPlus,
  Mail
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import HelpChatbot from './HelpChatbot';

const SECTION_GROUPS = [
  {
    label: 'Core',
    items: [
      { id: 'workflow', label: 'Task Workflow', icon: ArrowRight },
      { id: 'permissions', label: 'Role Permissions', icon: User },
      { id: 'constraints', label: 'Task Constraints', icon: AlertTriangle },
    ],
  },
  {
    label: 'Features',
    items: [
      { id: 'features', label: 'Key Features', icon: Zap },
      { id: 'dashboard', label: 'Enhanced Dashboard', icon: Activity },
      { id: 'quick-access', label: 'Quick Access Panel', icon: Zap },
      { id: 'documentation', label: 'Documentation System', icon: FileText },
      { id: 'notes', label: 'Notes System', icon: MessageSquare },
      { id: 'messages', label: 'Platform Messages', icon: Mail },
    ],
  },
  {
    label: 'Tracking',
    items: [
      { id: 'leave-management', label: 'Leave Management', icon: Calendar },
      { id: 'smart-idle', label: 'Smart Idle Detection', icon: Gauge },
      { id: 'meeting-time-tracking', label: 'Meeting Tracking', icon: Clock },
      { id: 'weekly-hours', label: 'Weekly Hours Enforcement', icon: Target },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { id: 'github', label: 'GitHub Integration', icon: Github },
      { id: 'github-advanced', label: 'Advanced GitHub', icon: Zap },
      { id: 'calendar', label: 'Calendar Integration', icon: Calendar },
      { id: 'phase4-features', label: 'Advanced Features', icon: Rocket },
    ],
  },
  {
    label: 'Security',
    items: [
      { id: 'two-factor', label: 'Two-Factor Auth', icon: Shield },
      { id: 'email-notifications', label: 'Email Notifications', icon: MessageSquare },
    ],
  },
];

const ADMIN_SECTION_GROUP = {
  label: 'Administration',
  items: [
    { id: 'analytics', label: 'Analytics & Metrics', icon: Gauge },
    { id: 'resource-utilization', label: 'Resource Utilization', icon: Users },
    { id: 'ai-validation', label: 'AI Validation', icon: Brain },
  ],
};

const HelpGuidelines = () => {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState('chat');
  const [activeSection, setActiveSection] = useState('workflow');
  const [docSearch, setDocSearch] = useState('');
  const [sectionKey, setSectionKey] = useState(0);

  const switchSection = (id) => {
    setActiveSection(id);
    setDocSearch('');
    setSectionKey((k) => k + 1);
  };

  // Redirect to workflow if member tries to access admin-only sections
  React.useEffect(() => {
    if ((user?.role === 'member') && (activeSection === 'analytics' || activeSection === 'resource-utilization')) {
      setActiveSection('workflow');
    }
  }, [user?.role, activeSection]);

  const sectionGroups = user?.role === 'admin'
    ? [...SECTION_GROUPS, ADMIN_SECTION_GROUP]
    : SECTION_GROUPS;

  const filteredGroups = docSearch.trim()
    ? sectionGroups.map(g => ({
        ...g,
        items: g.items.filter(s => s.label.toLowerCase().includes(docSearch.toLowerCase()))
      })).filter(g => g.items.length > 0)
    : sectionGroups;

  const workflowSteps = [
    {
      status: 'To Do',
      description: 'Task is created and assigned (you only see tasks assigned to you)',
      memberAction: 'Can start working',
      adminAction: 'Can assign, modify, or delete'
    },
    {
      status: 'In Progress',
      description: 'Member is actively working',
      memberAction: 'Can use timer, log time, add comments',
      adminAction: 'Can monitor progress, reassign'
    },
    {
      status: 'In QA',
                      description: 'Task is ready for review (requires BOTH screenshots AND time logging)',
      memberAction: 'Cannot modify task anymore',
      adminAction: 'Can review and approve or send back'
    },
    {
      status: 'Done',
                      description: 'Task is completed (requires BOTH screenshots AND time logging)',
      memberAction: 'Cannot modify task anymore',
      adminAction: 'Can reopen or archive'
    }
  ];

  const rolePermissions = [
    {
      role: 'Admin',
      icon: Crown,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      permissions: [
        'Full system access and control',
        'Can change any task status',
        'Can assign/unassign tasks',
        'Can modify completed tasks',
        'Can delete tasks and time entries',
        'Can manage team members (invite, approve, decline, change roles)',
        'Can approve or decline pending user registrations',
        'Can change user roles and manage user statuses',
        'Can view all analytics and reports',
        'Can override most constraints',
        'Must follow QA/Done status requirements (BOTH screenshots AND time logging)'
      ]
    },

    {
      role: 'Project Lead',
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      permissions: [
        'Can view all projects and tasks',
        'Can only edit/delete projects and tasks they created',
        'Can assign tasks to team members and change task statuses',
        'Can invite new members (member role only)',
        'Can view team members and pending users (read-only)',
        'Cannot approve/decline users or change user roles',
        'Cannot view team analytics and reports (admin only)',
        'Can manage documentation for projects they created',
        'Must follow QA/Done status requirements (BOTH screenshots AND time logging)'
      ]
    },

    {
      role: 'Member',
      icon: User,
      color: 'text-secondary-600',
      bgColor: 'bg-secondary-50',
      permissions: [
        'Can only move tasks: To Do → In Progress → In QA',
        'Cannot modify tasks in "In QA" or "Done" status',
        'Can use timer and log time',
        'Can add comments and attachments',
        'Can delete own comments within 24 hours of task completion',
        'Can view own time entries and reports',
        'Cannot access team analytics',
        'Limited time entry deletion (24-hour rule)',
        'Must provide BOTH screenshots AND time logging before moving tasks to QA/Done'
      ]
    }
  ];

  const taskConstraints = [
    {
      title: 'Status Change Restrictions',
      description: 'Members have limited ability to change task status',
      details: [
        'Can only move from "To Do" to "In Progress"',
        'Can only move from "In Progress" to "In QA"',
        'Cannot move tasks back to previous statuses',
        'Cannot change status once task is "In QA" or "Done"',
        'QA and Done status require BOTH screenshots AND time logging',
        'Subtask completion required before moving parent task to QA'
      ],
      icon: AlertTriangle,
      color: 'text-orange-600'
    },
    {
      title: 'QA & Done Status Requirements',
      description: 'Tasks cannot be moved to QA or Done status without proper documentation',
      details: [
        'Moving to "QA", "In QA", or "Done" status requires BOTH screenshots AND time logging',
        'This applies to all users regardless of role (admin, project lead, member)',
        'Screenshots provide visual evidence of completed work',
        'Time logging ensures proper time tracking and accountability',
        'Both main tasks and subtasks follow these rules',
        'Validation prevents premature task completion without documentation'
      ],
      icon: Shield,
      color: 'text-blue-600'
    },
    {
      title: 'Completed Task Restrictions',
      description: 'When a task is marked as "Done", members cannot:',
      details: [
        'Change task status',
        'Start or stop timer',
        'Log additional time',
        'Add new comments',
        'Upload attachments',
        'Delete screenshots'
      ],
      icon: CheckCircle,
      color: 'text-green-600'
    },
    {
      title: 'Timer & Time Tracking',
      description: 'Rules for time tracking and timer usage',
      details: [
        'Only one timer can be active at a time',
        'Timer automatically stops when switching tasks',
        'Time entries are permanent and cannot be deleted by members',
        'Timer shows elapsed time in real-time',
        'All timestamps are stored and displayed in UTC+5'
      ],
      icon: Clock,
      color: 'text-blue-600'
    },
    {
      title: '24-Hour Edit & Deletion Rule',
      description: 'Time entry and comment editing/deletion restrictions for members',
      details: [
        'Members can only edit or delete time entries within 24 hours after task completion',
        'Members can only delete their own comments within 24 hours after task completion',
        'Applies when task is moved to "Done" or "In QA" status',
        'After 24 hours, only admins can edit or delete time entries and comments',
        'Rule is bypassed if task status changes back to "In Progress"',
        'This ensures data integrity for completed work',
        'Comment deletion follows the same 24-hour constraint as time entries'
      ],
      icon: Timer,
      color: 'text-red-600'
    },
    {
      title: 'Dashboard Task Counts',
      description: 'How Active, Completed, and Late tasks are counted',
      details: [
        'Active Tasks: Only tasks assigned to you where status is anything except "Done"',
        'Completed Tasks: Only tasks assigned to you where status is exactly "Done"',
        'Tasks Completed Late: Only tasks assigned to you where status is "Done" and completed after due date',
        'All dashboard and task list counts are based on tasks where you are assigned (assigned_to includes you)'
      ],
      icon: Info,
      color: 'text-blue-600'
    },
    {
      title: 'Late Completion Tracking',
      description: 'System automatically tracks tasks completed after due date',
      details: [
        'Tasks completed after due date are marked as "completed_late"',
        'Late completions are tracked in analytics and member dashboards',
        'Late completion badges appear on task details and reports',
        'You only see late completions for tasks assigned to you',
        'Admin can view late completion metrics in analytics dashboard'
      ],
      icon: Calendar,
      color: 'text-purple-600'
    },
    {
      title: 'Project Lead Responsibilities',
      description: 'Specific constraints and responsibilities for Project Leads',
      details: [
        'Can view all projects and tasks in the system',
        'Can only edit/delete projects and tasks they personally created',
        'Can assign tasks to team members and change task statuses',
        'Can invite new members (member role only) and view team members',
        'Cannot approve/decline users or change user roles (admin only)',
        'Cannot view team analytics and reports (admin only)',
        'Can manage documentation for projects they created',
        'Responsible for project success and timely delivery',
        'Must monitor task progress and provide guidance to team members',
        'Should escalate complex issues to admin when needed'
      ],
      icon: Shield,
      color: 'text-blue-600'
    },
    {
      title: 'Task Creation Requirements',
      description: 'Mandatory fields and validation rules for creating tasks',
      details: [
        'Due Date is required for all tasks (marked with *)',
        'Estimated Hours is required for all tasks (marked with *)',
        'Project selection is required when creating new tasks',
        'Task selection is required when creating time entries',
        'Description is required for all tasks and time entries',
        'These requirements ensure proper task planning and time management'
      ],
      icon: FileText,
      color: 'text-green-600'
    },
    {
      title: 'Admin Team Member Assignment',
      description: 'Special rules for admin users when assigning team members',
      details: [
        'Admins can assign themselves to projects and tasks',
        'Admin names are visible to other admins in assignment lists',
        'Regular users cannot see admin names for privacy',
        'Admins can see all team members including other admins',
        'Self-assignment helps admins manage their own workload',
        'Privacy protection ensures admin information is not exposed to regular users'
      ],
      icon: Users,
      color: 'text-purple-600'
    },
    {
      title: 'Team Member Assignment Features',
      description: 'Enhanced functionality for assigning team members to projects and tasks',
      details: [
        'Search functionality available in team member assignment lists',
        'Search by name, email, username, or role',
        'Clear search button to reset filters quickly',
        'Real-time filtering as you type',
        'No more scrolling through long member lists',
        'Available in project creation, task creation, and assignment forms'
      ],
      icon: Search,
      color: 'text-indigo-600'
    },
    {
      title: 'Form Validation & Error Handling',
      description: 'Comprehensive validation rules and user feedback',
      details: [
        'Required fields are marked with asterisk (*)',
        'Real-time validation with clear error messages',
        'Cannot submit forms without required information',
        'Helpful notes explain requirements before submission',
        'Validation applies to all users regardless of role',
        'Consistent error handling across all forms'
      ],
      icon: AlertCircle,
      color: 'text-red-600'
    },
    {
      title: 'Team Management Permissions',
      description: 'Role-based access control for team management functions',
      details: [
        'Admins: Full access to invite, approve, decline, and manage all user roles',
        'Project Leads: Can invite new members (member role only) and view team members',
        'Project Leads: Can view pending users (read-only) but cannot approve/decline them',
        'Project Leads: Cannot approve users, change roles, or manage user statuses',
        'Members: No access to team management functions',
        'User approval process is restricted to administrators only',
        'Role escalation (promoting to project lead or admin) requires admin privileges',
        'This ensures security while allowing appropriate team growth capabilities'
      ],
      icon: Users,
      color: 'text-indigo-600'
    },
    {
      title: 'GitHub Integration Constraints',
      description: 'Limitations and considerations for GitHub integration',
      details: [
        'Integration only tracks time when actively working with connected repositories',
        'Time tracking requires active GitHub activity (commits, PRs, issues)',
        'Cannot track time for repositories not connected to your account',
        'Integration may have delays in syncing recent GitHub activities',
        'OAuth tokens expire and need periodic renewal',
        'Some GitHub features may not be fully supported in time tracking'
      ],
      icon: Github,
      color: 'text-purple-600'
    },
    {
      title: 'Calendar Integration Constraints',
      description: 'Limitations and considerations for Microsoft Calendar integration',
      details: [
        'Integration only tracks time for calendar events when connected',
        'Time tracking requires active calendar events and meetings',
        'Cannot track time for calendars not connected to your account',
        'Integration may have delays in syncing recent calendar changes',
        'OAuth tokens expire and need periodic renewal',
        'Some calendar features may not be fully supported in time tracking'
      ],
      icon: Calendar,
      color: 'text-blue-600'
    }
  ];

  const microsoftCalendarGuide = [
    {
      title: 'Outlook Calendar Connection',
      description: 'Secure integration with Microsoft 365 and Outlook calendars',
      details: [
        'Navigate to Profile → Integrations tab',
        'Click "Connect" button for Microsoft Calendar integration',
        'Sign in with your Microsoft 365 or Outlook account',
        'Grant calendar access permissions to the app',
        'Connection is stored securely with encrypted tokens',
        'Can disconnect at any time from the same interface'
      ],
      icon: Calendar,
      color: 'text-blue-600'
    },
    {
      title: 'Automatic Meeting Tracking',
      description: 'Automatically track time spent in meetings and events',
      details: [
        'Calendar events are automatically detected and tracked',
        'Meeting duration is calculated and logged as time entries',
        'Support for recurring meetings and all-day events',
        'Handles meeting cancellations and rescheduling',
        'Tracks both online and in-person meetings',
        'Integrates with Teams, Zoom, and other meeting platforms'
      ],
      icon: Clock,
      color: 'text-green-600'
    },
    {
      title: 'Event Correlation & Billing',
      description: 'Link calendar events to time entries for accurate billing',
      details: [
        'Automatically create time entries for calendar events',
        'Link meetings to specific projects and tasks',
        'Generate accurate billing for client meetings',
        'Track meeting preparation and follow-up time',
        'Export meeting time reports for invoicing',
        'Maintain audit trail of meeting-related activities'
      ],
      icon: Target,
      color: 'text-purple-600'
    },
    {
      title: 'Meeting Analytics & Insights',
      description: 'Monitor meeting patterns and time allocation',
      details: [
        'Track meeting frequency and duration patterns',
        'Analyze time spent in different types of meetings',
        'Identify meeting efficiency and productivity trends',
        'Monitor meeting-to-work time ratios',
        'Generate reports on meeting time allocation',
        'Identify opportunities for meeting optimization'
      ],
      icon: TrendingUp,
      color: 'text-orange-600'
    },
    {
      title: 'Calendar Synchronization',
      description: 'Real-time synchronization with Outlook calendar',
      details: [
        'Automatic sync every 15 minutes for real-time updates',
        'Handles calendar changes, additions, and deletions',
        'Syncs across multiple devices and platforms',
        'Maintains data consistency across all integrations',
        'Supports multiple calendar accounts and shared calendars',
        'Handles timezone differences automatically'
      ],
      icon: Zap,
      color: 'text-indigo-600'
    },
    {
      title: 'Meeting Management Features',
      description: 'Advanced features for managing meeting time tracking',
      details: [
        'Filter and categorize meetings by type and project',
        'Set meeting time tracking preferences and rules',
        'Exclude specific meeting types from time tracking',
        'Customize meeting time entry descriptions',
        'Bulk edit meeting time entries when needed',
        'Set reminders for meeting time logging'
      ],
      icon: Settings,
      color: 'text-teal-600'
    },
    {
      title: 'Troubleshooting & Common Issues',
      description: 'Solutions for common calendar integration problems',
      details: [
        'If connection fails, check Microsoft account permissions',
        'Ensure calendar sharing permissions are properly set',
        'Check internet connection and Microsoft service status',
        'Clear browser cache if integration page doesn\'t load',
        'Verify calendar access in Microsoft 365 settings',
        'Contact admin if persistent connection issues occur'
      ],
      icon: AlertCircle,
      color: 'text-red-600'
    },
    {
      title: 'Security & Privacy',
      description: 'Understanding data security and privacy in calendar integration',
      details: [
        'OAuth tokens are encrypted and stored securely',
        'App only requests read access to calendar events',
        'No calendar modifications or write permissions granted',
        'Personal calendar data remains within your Microsoft account',
        'Integration can be disabled at any time',
        'All API calls use secure HTTPS connections'
      ],
      icon: Lock,
      color: 'text-indigo-600'
    },
    {
      title: 'Best Practices for Calendar Integration',
      description: 'Tips for maximizing the value of calendar integration',
      details: [
        'Use descriptive meeting titles for better time tracking',
        'Set up recurring meetings for consistent tracking',
        'Regularly review and categorize meeting time entries',
        'Monitor meeting efficiency metrics and trends',
        'Integrate with project management for better correlation',
        'Combine with manual time logging for comprehensive tracking'
      ],
      icon: Lightbulb,
      color: 'text-yellow-600'
    },
    {
      title: 'Team Collaboration Features',
      description: 'How calendar integration enhances team productivity',
      details: [
        'Track team meeting time and collaboration patterns',
        'Monitor project meeting allocation and efficiency',
        'Identify meeting-heavy periods and optimize schedules',
        'Generate team meeting productivity reports',
        'Track client meeting time for project billing',
        'Optimize team communication and meeting practices'
      ],
      icon: Users,
      color: 'text-cyan-600'
    }
  ];

  const keyFeatures = [
    {
      title: 'Real-time Timer',
      icon: Play,
      description: 'Start/stop timer to track work time automatically',
      color: 'text-green-600'
    },
    {
      title: 'Team Member Search',
      icon: Search,
      description: 'Quickly find team members when assigning to projects and tasks',
      color: 'text-indigo-600'
    },
    {
      title: 'Manual Time Logging',
      icon: Clock,
      description: 'Log time manually for past work sessions',
      color: 'text-blue-600'
    },
    {
      title: 'Comments & Mentions',
      icon: MessageSquare,
      description: 'Add comments and mention team members with @',
      color: 'text-purple-600'
    },
    {
      title: 'Platform Messages',
      icon: Mail,
      description: 'DMs, groups, pins, GIFs, voice notes, and realtime chat',
      color: 'text-sky-600'
    },
    {
      title: 'File Attachments',
      icon: Paperclip,
      description: 'Upload screenshots and files to tasks',
      color: 'text-orange-600'
    },
    {
      title: 'Activity Tracking',
      icon: Zap,
      description: 'All actions are logged in activity feed',
      color: 'text-indigo-600'
    },
    {
      title: 'Efficiency Metrics',
      icon: Target,
      description: 'Track estimated vs actual time for tasks',
      color: 'text-teal-600'
    },
    {
      title: 'GitHub Integration',
      icon: Github,
      description: 'Connect GitHub account for development tracking',
      color: 'text-gray-600'
    },
    {
      title: 'Commit Time Tracking',
      icon: Github,
      description: 'Automatically track time spent on specific commits and branches',
      color: 'text-green-600'
    },
    {
      title: 'Pull Request Monitoring',
      icon: Github,
      description: 'Track time spent on code reviews and pull request activities',
      color: 'text-blue-600'
    },
    {
      title: 'Two-Factor Auth',
      icon: Shield,
      description: 'Enhanced security with TOTP authentication',
      color: 'text-red-600'
    },
    {
      title: 'Automated Email Notifications',
      icon: MessageSquare,
      description: 'Smart notifications for overdue tasks, due dates, and weekly summaries',
      color: 'text-purple-600'
    },
    {
      title: 'Microsoft Calendar Integration',
      icon: Calendar,
      description: 'Connect Outlook calendar for automatic meeting time tracking',
      color: 'text-blue-600'
    },
    {
      title: 'Meeting Time Tracking',
      icon: Clock,
      description: 'Automatically track time spent in meetings and events',
      color: 'text-green-600'
    },
    {
      title: 'Smart Idle Detection',
      icon: Gauge,
      description: 'Timer continues running when working in VS Code, YouTube, or other productive applications',
      color: 'text-indigo-600'
    },

    ...(user?.role === 'admin' ? [{
      title: 'Idle Detection',
      icon: Gauge,
      description: 'Monitor productivity by tracking idle time during work sessions',
      color: 'text-purple-600'
    }] : [])
  ];

  const analyticsMetrics = [
    {
      title: 'Efficiency Tracking',
      description: 'Measure how well tasks are completed vs estimates',
      details: [
        'Efficiency = (Estimated Hours / Actual Hours) × 100',
        'Efficiency > 100%: Task completed faster than estimated',
        'Efficiency < 100%: Task took longer than estimated',
        'Helps identify members who may need better estimation skills',
        'Admin can view per-task and per-member efficiency metrics'
      ],
      icon: Target,
      color: 'text-green-600'
    },
    {
      title: 'Utilization Metrics',
      description: 'Track member workload and utilization levels',
      details: [
        'Overloaded Members: Working >40 hours/week equivalent',
        'Underutilized Members: Working <40 hours/week equivalent',
        'Target utilization: 40 hours/week for optimal productivity',
        'Helps with workload distribution and resource planning',
        'Admins can identify members who need more or fewer tasks'
      ],
      icon: TrendingUp,
      color: 'text-blue-600'
    },
    {
      title: 'Performance Tracking',
      description: 'Monitor member performance and productivity',
      details: [
        'Weekly hours tracking with 40-hour target',
        'Daily hours tracking with 8-hour target',
        'Performance percentage based on target hours',
        'Top performers and performance issues identification',
        'Overdue tasks and late completion tracking',
        'Action Alerts panel: under-40h this week and chronic under-hours (last 4 weeks)',
        'Weekly Hours Enforcement emails complement Action Alerts (see Tracking → Weekly Hours Enforcement)'
      ],
      icon: Gauge,
      color: 'text-purple-600'
    },
    {
      title: 'Idle Detection & Productivity Monitoring',
      description: 'Track and analyze idle time during work sessions',
      details: [
        'Idle detection automatically tracks periods of inactivity during timer sessions',
        'Idle time is calculated as percentage of total tracked time',
        'Green indicators: < 2h idle time, < 15% idle percentage (Good productivity)',
        'Yellow indicators: 2-5h idle time, 15-30% idle percentage (Moderate concern)',
        'Red indicators: > 5h idle time, > 30% idle percentage (High concern)',
        'High idle entries (>20% idle) are flagged for admin review',
        'Idle metrics help identify productivity patterns and support needs',
        'Available in analytics dashboard, user details, and project analytics'
      ],
      icon: Gauge,
      color: 'text-purple-600'
    },
    {
      title: 'Timezone & Data Integrity',
      description: 'All timestamps and analytics use UTC+5 timezone',
      details: [
        'All server timestamps stored in UTC timezone',
        'Frontend displays times in UTC (no timezone offset)',
        'Consistent time tracking across all users globally',
        'Analytics calculations use UTC for accurate reporting',
        'Activity logs use server UTC time for consistency'
      ],
      icon: Clock,
      color: 'text-orange-600'
    }
  ];

  const meetingTimeTrackingGuide = [
    {
      title: 'Enable Meeting Time Tracking',
      description: 'Set up automatic meeting time tracking in your profile',
      details: [
        'Navigate to Profile → Microsoft Calendar Integration section',
        'Toggle "Meeting Time Tracking" switch to enable the feature',
        'Ensure Microsoft Calendar is connected first (required)',
        'The system will create a "General Meeting - Your Name" project automatically',
        'You will receive a welcome email when the project is created',
        'Meeting time tracking only works for users with calendar connected'
      ],
      icon: Settings,
      color: 'text-blue-600'
    },
    {
      title: 'How It Works',
      description: 'Automatic detection and time entry creation for meetings',
      details: [
        'System runs every 2 hours from 10 AM to 8 PM to detect ended meetings',
        'Only tracks meetings lasting 15+ minutes',
        'Waits 5 minutes after meeting ends before creating time entry',
        'Only creates entries for meetings you accepted or organized',
        'Processes meetings from the last 2 hours (not just today)',
        'Meeting organizers are automatically considered "accepted"',
        'Prevents duplicate time entries for the same meeting'
      ],
      icon: Clock,
      color: 'text-green-600'
    },
    {
      title: 'Meeting Criteria',
      description: 'What meetings qualify for automatic time tracking',
      details: [
        'Meeting duration: Must be 15+ minutes long',
        'Meeting status: You must have accepted or organized the meeting',
        'Time window: Only meetings ended in the last 2 hours',
        'End time validation: 5-minute delay after meeting ends',
        'Duplicate prevention: Won\'t create duplicate entries',
        'Admin exclusion: Admin users are excluded from automatic tracking',
        'Calendar requirement: Microsoft Calendar must be connected'
      ],
      icon: CheckCircle,
      color: 'text-purple-600'
    },
    {
      title: 'Project Management',
      description: 'Automatic project creation and management for meetings',
      details: [
        'Creates "General Meeting - Your Name" project automatically',
        'Project is created only once per user (on first enable)',
        'All meeting time entries are linked to this project',
        'Project appears in your Projects list',
        'You can edit project details if needed',
        'Project creation triggers welcome email notification',
        'Project is user-specific and private to you'
      ],
      icon: FolderPlus,
      color: 'text-orange-600'
    },
    {
      title: 'Email Notifications',
      description: 'Professional email notifications for meeting activities',
      details: [
        'Project Creation Email: Welcome email when meeting project is created',
        'Time Entry Email: Notification when meeting time entry is created',
        'Professional Templates: Consistent design matching system standards',
        'Feature Explanation: Clear explanation of what the system does',
        'Email Timing: Sent immediately when events occur',
        'Email Content: Includes meeting details and time information',
        'Unsubscribe: Can be disabled in email preferences'
      ],
      icon: Mail,
      color: 'text-indigo-600'
    }
  ];

  const resourceUtilizationGuide = [
    {
      title: 'Resource Overview',
      description: 'Comprehensive view of all team members and their utilization',
      details: [
        'View all team members with their current project assignments',
        'See department, current team, and future project assignments',
        'Track number of tasks assigned to each member',
        'Monitor team lead assignments and comments',
        'Filter resources by department, current projects, and team leads'
      ],
      icon: Users,
      color: 'text-blue-600'
    },
    {
      title: 'Resource Editing & Management',
      description: 'Update resource information and project assignments',
      details: [
        'Edit department assignments for team members',
        'Assign future projects to team members (multi-select available)',
        'Update team lead assignments from admin users',
        'Add comments for resource planning and notes',
        'Changes are saved immediately and reflected in the system',
        'Future projects are stored as comma-separated project IDs'
      ],
      icon: Edit,
      color: 'text-green-600'
    },
    {
      title: 'Workload Distribution',
      description: 'Monitor and balance team workload effectively',
      details: [
        'View current project assignments for each team member',
        'See task counts to identify overloaded or underutilized members',
        'Use filters to find members by department or current projects',
        'Identify members who need additional tasks or support',
        'Plan future project assignments based on current workload',
        'Track team lead assignments to ensure proper supervision'
      ],
      icon: TrendingUp,
      color: 'text-purple-600'
    },
    {
      title: 'Resource Planning',
      description: 'Strategic planning for future project assignments',
      details: [
        'Assign multiple future projects to team members',
        'Use multi-select dropdown to choose from available projects',
        'Plan resource allocation for upcoming projects',
        'Track which members are available for new assignments',
        'Maintain historical record of project assignments',
        'Support for both current and future project tracking'
      ],
      icon: Calendar,
      color: 'text-orange-600'
    },
    {
      title: 'Search & Filtering',
      description: 'Efficiently find and filter team resources',
      details: [
        'Search resources by name or email address',
        'Filter by department to view specific teams',
        'Filter by current projects to see project-specific resources',
        'Filter by team lead to view supervised teams',
        'Clear filters to reset to full resource list',
        'Pagination support for large resource lists'
      ],
      icon: Search,
      color: 'text-indigo-600'
    },
    {
      title: 'Data Management',
      description: 'Understanding how resource data is stored and managed',
      details: [
        'Resource data is stored in user profiles with role "member"',
      ],
      icon: Database,
      color: 'text-gray-600'
    }
  ];

  const aiValidationGuide = [
    {
      title: 'AI-Powered Time Tracking Validation',
      description: 'Intelligent validation of time entries using machine learning',
      details: [
        'Automatically detects unusual session durations and patterns',
        'Identifies overlapping time entries and potential double-booking',
        'Analyzes idle time percentages and productivity patterns',
        'Provides confidence scores and risk level assessments',
        'Generates actionable recommendations for improvement',
        'Learns from user patterns to improve accuracy over time'
      ],
      icon: Brain,
      color: 'text-purple-600'
    },
    {
      title: 'Task Estimation Validation',
      description: 'AI-powered validation of task time estimates',
      details: [
        'Compares estimates with similar completed tasks',
        'Analyzes task complexity based on description and requirements',
        'Considers assignee experience and historical performance',
        'Provides suggested adjustments and confidence scores',
        'Identifies estimation bias patterns (over/under-estimation)',
        'Helps improve estimation accuracy over time'
      ],
      icon: Target,
      color: 'text-blue-600'
    },
    {
      title: 'Anomaly Detection',
      description: 'Advanced detection of unusual patterns and potential issues',
      details: [
        'Detects sessions longer than 12 hours automatically',
        'Flags overlapping time entries for review',
        'Identifies high idle time sessions (>30% idle)',
        'Monitors for irregular work patterns vs. user history',
        'Provides real-time warnings and suggestions',
        'Helps maintain data integrity and accuracy'
      ],
      icon: AlertCircle,
      color: 'text-red-600'
    },
    {
      title: 'Productivity Insights',
      description: 'AI-generated insights for improving productivity',
      details: [
        'Identifies peak productivity hours for each user',
        'Analyzes work patterns and suggests optimizations',
        'Tracks estimation accuracy trends over time',
        'Provides personalized recommendations for improvement',
        'Monitors team performance and identifies areas for support',
        'Helps optimize work schedules and task assignments'
      ],
      icon: TrendingUp,
      color: 'text-green-600'
    },
    {
      title: 'Confidence Scoring',
      description: 'Reliability assessment for AI validation results',
      details: [
        'Confidence scores range from 0% to 100%',
        'High confidence (>80%): AI is very confident in validation',
        'Medium confidence (50-80%): Some uncertainty, review recommended',
        'Low confidence (<50%): High uncertainty, manual review needed',
        'Factors include data completeness and historical patterns',
        'Helps users understand AI recommendation reliability'
      ],
      icon: ShieldIcon,
      color: 'text-yellow-600'
    },
    {
      title: 'Risk Level Assessment',
      description: 'Categorized risk levels for validation results',
      details: [
        'Low Risk: Normal patterns, no action required',
        'Medium Risk: Some concerns, review recommended',
        'High Risk: Significant issues, immediate attention needed',
        'Risk levels are based on anomaly scores and patterns',
        'Provides clear guidance on when to take action',
        'Helps prioritize validation review efforts'
      ],
      icon: AlertTriangle,
      color: 'text-orange-600'
    },
    {
      title: 'How to Use AI Validation',
      description: 'Step-by-step guide for using AI validation features',
      details: [
        'Navigate to "AI Validation" in the sidebar (Admin only)',
        'Select "Time Tracking Validation" to validate time entries',
        'Select "Task Estimation Validation" to validate task estimates',
        'Review confidence scores, warnings, and suggestions',
        'Take action based on risk levels and recommendations',
        'Use insights to improve time tracking and estimation accuracy'
      ],
      icon: Lightbulb,
      color: 'text-indigo-600'
    },
    {
      title: 'Best Practices for AI Validation',
      description: 'Guidelines for effective use of AI validation features',
      details: [
        'Validate time entries within 24 hours for best results',
        'Review high-risk items immediately',
        'Use AI suggestions as guidance, not absolute rules',
        'Provide feedback on AI accuracy to improve the system',
        'Combine AI insights with human judgment for best decisions',
        'Regularly review validation patterns to identify trends'
      ],
      icon: Activity,
      color: 'text-teal-600'
    }
  ];

  const githubIntegrationGuide = [
    {
      title: 'GitHub Account Connection',
      description: 'Secure OAuth-based connection to your GitHub account',
      details: [
        'Navigate to Profile → Integrations tab',
        'Click "Connect" button for GitHub integration',
        'Authorize the app to access your GitHub account',
        'Grant repository and user information access',
        'Connection is stored securely with encrypted tokens',
        'Can disconnect at any time from the same interface'
      ],
      icon: Github,
      color: 'text-gray-600'
    },
    {
      title: 'Repository Management',
      description: 'Connect to existing repositories or create new ones',
      details: [
        'Connect to your existing GitHub repositories',
        'Create new repositories directly from the app',
        'Access both user and organization repositories',
        'Automatic branch creation (defaults to main)',
        'Manage repository settings and permissions',
        'View repository activity and commit history'
      ],
      icon: Database,
      color: 'text-blue-600'
    },
    {
      title: 'Branch Management',
      description: 'Track time across different branches and features',
      details: [
        'Create and manage feature branches',
        'Track time spent on specific branches',
        'Switch between branches for different features',
        'Monitor branch-specific development progress',
        'Link commits to specific branch work',
        'Track feature development across branches'
      ],
      icon: GitBranch,
      color: 'text-green-600'
    },
    {
      title: 'Development Time Tracking',
      description: 'Link your development work to time entries',
      details: [
        'Track time spent on specific repositories',
        'Link commits to time entries for accurate billing',
        'Create time entries directly from commits',
        'Monitor development progress and activity',
        'View commit history and time correlation',
        'Export development time reports with commit data'
      ],
      icon: Clock,
      color: 'text-purple-600'
    },
    {
      title: 'Smart Commit Integration',
      description: 'Advanced commit linking and suggestions',
      details: [
        'Smart commit suggestions based on user activity',
        'Branch-aware commit filtering and suggestions',
        'Commit message search and fuzzy matching',
        'Automatic commit metadata extraction',
        'Link commits to time entries with one click',
        'Track commit author, date, and branch information'
      ],
      icon: Zap,
      color: 'text-yellow-600'
    },
    {
      title: 'Enhanced Comment System',
      description: 'Advanced commenting with GitHub context',
      details: [
        'Smart linking: @commit:abc1234, @issue:#123, @pr:#456',
        'File-specific comments: @file:src/main.js',
        'Context on hover: commit diff, file changes preview',
        'GitHub metadata: author info, timestamps, branch info',
        'Commit attachment in task comments',
        'Keyboard shortcuts for quick GitHub operations'
      ],
      icon: MessageSquare,
      color: 'text-indigo-600'
    },
    {
      title: 'Collaborator Management',
      description: 'Manage team access and permissions',
      details: [
        'Add team members as repository collaborators',
        'Role-based access control for different user types',
        'View all collaborators in a dedicated modal',
        'Remove collaborators with confirmation workflow',
        'Admin-only: disconnect repositories and manage settings',
        'Project Leads and Members: view-only access to settings'
      ],
      icon: Users,
      color: 'text-orange-600'
    },
    {
      title: 'Release Management',
      description: 'Track features through commits and deployments',
      details: [
        'Create release features linked to commits',
        'Track feature deployment status and progress',
        'Monitor release versions and rollback status',
        'Link commits to specific release features',
        'Track deployment time and success rates',
        'Generate release reports and analytics'
      ],
      icon: Target,
      color: 'text-red-600'
    },
    {
      title: 'Code Review Integration',
      description: 'Link PRs to task reviews and track workflows',
      details: [
        'Link GitHub pull requests to task reviews',
        'Track review status: pending, in progress, completed',
        'Monitor code review time and collaboration',
        'Update PR status and review completion',
        'Track review quality metrics and patterns',
        'Generate code review reports and insights'
      ],
      icon: CheckCircle,
      color: 'text-teal-600'
    },
    {
      title: 'Activity Logging & Analytics',
      description: 'Comprehensive tracking of all GitHub activities',
      details: [
        'All repository operations are logged with context',
        'Real-time activity feed updates',
        'Track repository connections and disconnections',
        'Monitor branch updates and changes',
        'Log collaborator additions and removals',
        'Generate GitHub activity reports and metrics'
      ],
      icon: Activity,
      color: 'text-cyan-600'
    },
    {
      title: 'Permission-Based Access Control',
      description: 'Role-based restrictions for different user types',
      details: [
        'Admins: Full access to all GitHub operations',
        'Project Leads: Can change branches, view collaborators',
        'Members: View-only access to GitHub integration',
        'Cannot disconnect repositories (admin only)',
        'Cannot delete collaborators (admin only)',
        'View-only badges for non-admin users'
      ],
      icon: Shield,
      color: 'text-gray-600'
    },
    {
      title: 'Repository Access & Security',
      description: 'Control which repositories the app can access',
      details: [
        'App requests access to public and private repositories',
        'Can read repository information and commit history',
        'Cannot modify code or push changes',
        'Access is limited to user account scope',
        'Repository access can be revoked from GitHub settings',
        'OAuth tokens are automatically refreshed when needed'
      ],
      icon: Lock,
      color: 'text-indigo-600'
    },
    {
      title: 'Integration Benefits',
      description: 'How integrations improve your workflow',
      details: [
        'Automatic time tracking for development work',
        'Accurate billing based on actual commit work',
        'Project progress tracking through commit history',
        'Team collaboration insights and metrics',
        'Integration with existing GitHub workflows',
        'Enhanced reporting for development projects'
      ],
      icon: TrendingUp,
      color: 'text-purple-600'
    },
    {
      title: 'Troubleshooting & Common Issues',
      description: 'Solutions for common GitHub integration problems',
      details: [
        'If connection fails, check GitHub account permissions',
        'Revoke and reconnect if tokens become invalid',
        'Ensure GitHub account has necessary repository access',
        'Check internet connection and GitHub service status',
        'Clear browser cache if integration page doesn\'t load',
        'Contact admin if persistent connection issues occur'
      ],
      icon: AlertCircle,
      color: 'text-red-600'
    },
    {
      title: 'Best Practices for GitHub Integration',
      description: 'Tips for maximizing the value of GitHub integration',
      details: [
        'Connect repositories you actively work on',
        'Use descriptive commit messages for better time tracking',
        'Regularly sync to ensure accurate time correlation',
        'Monitor integration status in your profile',
        'Disconnect unused repositories to maintain clean data',
        'Combine with manual time logging for comprehensive tracking'
      ],
      icon: Lightbulb,
      color: 'text-teal-600'
    },
    {
      title: 'Team Collaboration Features',
      description: 'How GitHub integration enhances team productivity',
      details: [
        'View team member commit activity and time allocation',
        'Track project progress through repository activity',
        'Identify bottlenecks in development workflow',
        'Monitor code review and collaboration time',
        'Generate team productivity reports',
        'Optimize development processes based on data insights'
      ],
      icon: Users,
      color: 'text-orange-600'
    }
  ];

  const advancedGitHubFeatures = [
    {
      title: 'Smart Commit Suggestions',
      description: 'AI-powered commit recommendations and filtering',
      details: [
        'Auto-suggest recent commits based on user activity',
        'Branch-aware commit suggestions for feature development',
        'Commit message search with fuzzy matching',
        'SHA and message-based commit filtering',
        'User-specific commit recommendations',
        'Context-aware commit suggestions for current task'
      ],
      icon: Brain,
      color: 'text-purple-600'
    },
    {
      title: 'Enhanced Comment Types',
      description: 'Advanced commenting with GitHub context integration',
      details: [
        '@commit:abc1234 for direct commit linking',
        '@issue:#123 for issue references and tracking',
        '@pr:#456 for pull request integration',
        '@file:src/main.js for file-specific comments',
        'Context preview on hover for all GitHub references',
        'Automatic GitHub metadata extraction and display'
      ],
      icon: MessageSquare,
      color: 'text-blue-600'
    },
    {
      title: 'GitHub Context in Comments',
      description: 'Rich context and metadata display for GitHub references',
      details: [
        'Commit diff preview on hover',
        'File changes preview for file references',
        'Author information and timestamps from GitHub',
        'Branch comparison and context information',
        'Repository and organization context',
        'Real-time GitHub data synchronization'
      ],
      icon: Info,
      color: 'text-green-600'
    },
    {
      title: 'Time Tracking Integration',
      description: 'Seamless integration between commits and time entries',
      details: [
        'Create time entries directly from commits',
        'Automatic commit metadata in time entries',
        'Link time tracking to specific branches',
        'Track development time with commit context',
        'Generate time reports with GitHub data',
        'Monitor development velocity and productivity'
      ],
      icon: Clock,
      color: 'text-orange-600'
    },
    {
      title: 'Release Management',
      description: 'Track features and releases through commits',
      details: [
        'Create release features linked to commits',
        'Track feature deployment status and progress',
        'Monitor release versions and rollback status',
        'Link commits to specific release features',
        'Track deployment time and success rates',
        'Generate release reports and analytics'
      ],
      icon: Target,
      color: 'text-red-600'
    },
    {
      title: 'Code Review Integration',
      description: 'Link GitHub PRs to task reviews and track workflows',
      details: [
        'Link GitHub pull requests to task reviews',
        'Track review status: pending, in progress, completed',
        'Monitor code review time and collaboration',
        'Update PR status and review completion',
        'Track review quality metrics and patterns',
        'Generate code review reports and insights'
      ],
      icon: CheckCircle,
      color: 'text-teal-600'
    },
    {
      title: 'Collaborator Suggestions',
      description: 'Smart suggestions for team collaboration',
      details: [
        'Show project team members not yet in repository',
        'Suggest collaborators based on project assignments',
        'Role-based collaborator recommendations',
        'Automatic collaborator discovery',
        'Team member activity tracking',
        'Collaboration analytics and insights'
      ],
      icon: Users,
      color: 'text-indigo-600'
    },
    {
      title: 'Keyboard Shortcuts',
      description: 'Quick access to GitHub features and operations',
      details: [
        'Ctrl/Cmd + G: Open GitHub integration modal',
        'Ctrl/Cmd + Shift + G: Quick commit selection',
        'Ctrl/Cmd + Enter: Submit comments with GitHub context',
        'Tab navigation in GitHub modals',
        'Quick repository switching shortcuts',
        'Escape key for modal dismissal'
      ],
      icon: Zap,
      color: 'text-yellow-600'
    }
  ];

  const phase4Features = [
    {
      title: 'Automated Time Tracking',
      description: 'Link commits to time entries for accurate development tracking',
      details: [
        'Create time entries directly from GitHub commits',
        'Automatic commit metadata extraction (SHA, message, author, date)',
        'Branch-aware time tracking for feature development',
        'Link time entries to specific repository and branch',
        'Track development velocity and productivity patterns',
        'Generate time reports with commit context and links'
      ],
      icon: Clock,
      color: 'text-blue-600'
    },
    {
      title: 'Release Management',
      description: 'Track features through commits and manage deployment status',
      details: [
        'Create release features linked to specific commits',
        'Track feature deployment status: pending, in progress, deployed',
        'Monitor release versions and rollback status',
        'Link multiple commits to single release features',
        'Track deployment time and success rates',
        'Generate release reports with commit associations'
      ],
      icon: Target,
      color: 'text-green-600'
    },
    {
      title: 'Code Review Integration',
      description: 'Link GitHub PRs to task reviews and track review workflows',
      details: [
        'Create code reviews linked to GitHub pull requests',
        'Track review status: pending, in progress, completed',
        'Monitor code review time and collaboration patterns',
        'Update PR status and review completion status',
        'Track review quality metrics and team performance',
        'Generate code review reports and insights'
      ],
      icon: CheckCircle,
      color: 'text-purple-600'
    },
    {
      title: 'Enhanced Activity Tracking',
      description: 'Comprehensive logging of all GitHub and development activities',
      details: [
        'All GitHub operations logged with detailed context',
        'Real-time activity feed updates for GitHub events',
        'Track repository connections, disconnections, and updates',
        'Monitor branch changes and collaborator management',
        'Log time entry creation with commit linking',
        'Generate comprehensive development activity reports'
      ],
      icon: Activity,
      color: 'text-orange-600'
    },
    {
      title: 'Smart Commit Suggestions',
      description: 'AI-powered commit recommendations and filtering',
      details: [
        'Auto-suggest recent commits based on user activity',
        'Branch-aware commit suggestions for current task',
        'Commit message search with fuzzy matching',
        'User-specific commit filtering (your commits only)',
        'Context-aware commit recommendations',
        'Quick commit selection for time entry creation'
      ],
      icon: Brain,
      color: 'text-indigo-600'
    },
    {
      title: 'Advanced Comment System',
      description: 'Enhanced commenting with GitHub context and smart linking',
      details: [
        'Smart linking: @commit:abc1234, @issue:#123, @pr:#456',
        'File-specific comments: @file:src/main.js',
        'Context preview on hover for all GitHub references',
        'Commit attachment in task comments',
        'Keyboard shortcuts for quick GitHub operations',
        'Enhanced comment types with GitHub metadata'
      ],
      icon: MessageSquare,
      color: 'text-teal-600'
    }
  ];

  const twoFactorAuthGuide = [
    {
      title: 'Two-Factor Authentication Setup',
      description: 'Secure your account with TOTP-based 2FA',
      details: [
        'Navigate to Profile → Two-Factor Auth tab',
        'Click "Enable Two-Factor Authentication"',
        'Scan QR code with authenticator app (Google Authenticator, Authy)',
        'Enter 6-digit code from your app to verify',
        'Save backup codes for emergency access',
        '2FA is now active and required for login'
      ],
      icon: Shield,
      color: 'text-blue-600'
    },
    {
      title: 'Authenticator Apps',
      description: 'Recommended apps for 2FA setup',
      details: [
        'Google Authenticator (Android/iOS)',
        'Authy (cross-platform with cloud backup)',
        'Microsoft Authenticator',
        '1Password (if using password manager)',
        'Any TOTP-compatible authenticator app',
        'Ensure app supports time-based one-time passwords'
      ],
      icon: Smartphone,
      color: 'text-green-600'
    },
    {
      title: 'Backup Codes',
      description: 'Emergency access codes for account recovery',
      details: [
        'Generate 10 backup codes during 2FA setup',
        'Store codes securely (password manager, safe location)',
        'Each code can only be used once',
        'Use when authenticator app is unavailable',
        'Regenerate codes if compromised',
        'Keep backup codes separate from 2FA device'
      ],
      icon: Key,
      color: 'text-orange-600'
    },
    {
      title: 'Login Process',
      description: 'How 2FA works during authentication',
      details: [
        'Enter username and password as usual',
        'System prompts for 2FA code',
        'Open authenticator app and get current code',
        'Enter 6-digit code in the prompt',
        'Code is valid for 30 seconds',
        'Access granted after successful verification'
      ],
      icon: LogIn,
      color: 'text-purple-600'
    },
    {
      title: 'Managing 2FA',
      description: 'Enable, disable, and manage 2FA settings',
      details: [
        'View current 2FA status in profile',
        'Disable 2FA if needed (requires password confirmation)',
        'Regenerate backup codes when needed',
        'Change authenticator app if desired',
        'Monitor 2FA usage and security events',
        'Contact admin if 2FA device is lost'
      ],
      icon: Settings,
      color: 'text-indigo-600'
    },
    {
      title: 'Security Best Practices',
      description: 'Tips for maintaining 2FA security',
      details: [
        'Use dedicated device for authenticator app',
        'Keep backup codes in secure location',
        'Don\'t share 2FA codes with anyone',
        'Use strong, unique passwords',
        'Monitor account for suspicious activity',
        'Consider multiple 2FA methods if available'
      ],
      icon: Lock,
      color: 'text-red-600'
    }
  ];



  const renderSection = () => {
    switch (activeSection) {
      case 'workflow':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Task Workflow Guide</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Understand how tasks move through different stages</p>
            </div>
            
            <div className="space-y-4">
              {workflowSteps.map((step, index) => (
                <div key={step.status} className="card">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-secondary-900 dark:text-white">{step.status}</h3>
                        <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs font-medium">
                          {step.status}
                        </span>
                      </div>
                      <p className="text-secondary-600 mb-3">{step.description}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-green-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <User className="h-4 w-4 text-green-600" />
                            <span className="font-medium text-green-800">Member Actions</span>
                          </div>
                          <p className="text-sm text-green-700">{step.memberAction}</p>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Crown className="h-4 w-4 text-blue-600" />
                            <span className="font-medium text-blue-800">Admin Actions</span>
                          </div>
                          <p className="text-sm text-blue-700">{step.adminAction}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Additional Requirements Note */}
            <div className="card bg-blue-50 border-blue-200">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-2">Important Requirements</h3>
                  <div className="space-y-2 text-sm text-blue-800">
                    <p><strong>Due Date & Estimated Hours:</strong> Both fields are mandatory when creating tasks</p>
                    <p><strong>QA & Done Status:</strong> Tasks cannot be moved to these statuses without BOTH screenshots AND time logging</p>
                    <p><strong>Task Assignment:</strong> Task selection is required when creating time entries</p>
                    <p><strong>Validation:</strong> All users, including admins, must follow these requirements</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Notifications Note */}
            <div className="card bg-green-50 border-green-200">
              <div className="flex items-start gap-3">
                <MessageSquare className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-green-900 mb-2">Stay Informed with Email Notifications</h3>
                  <div className="space-y-2 text-sm text-green-800">
                    <p><strong>Daily Reminders:</strong> Get notified about overdue tasks (10 AM) and tasks due today (9 AM & 7 PM)</p>
                    <p><strong>Weekly Reports:</strong> Receive personal summaries every Monday with your progress and statistics</p>
                    <p><strong>Smart Scheduling:</strong> All emails are sent Monday-Friday during business hours only</p>
                    <p><strong>Privacy First:</strong> You only receive information about your own tasks and activities</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'permissions':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Role-Based Permissions</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Understand what each role can and cannot do</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {rolePermissions.map((role) => {
                const Icon = role.icon;
                return (
                  <div key={role.role} className={`card ${role.bgColor}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <Icon className={`h-6 w-6 ${role.color}`} />
                      <h3 className="font-semibold text-secondary-900 dark:text-white">{role.role}</h3>
                    </div>
                    <ul className="space-y-2">
                      {role.permissions.map((permission, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-secondary-700">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{permission}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'constraints':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Task Constraints & Limitations</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Important restrictions to be aware of</p>
            </div>
            
            <div className="space-y-6">
              {taskConstraints.map((constraint) => {
                const Icon = constraint.icon;
                return (
                  <div key={constraint.title} className="card">
                    <div className="flex items-start gap-4 mb-4">
                      <Icon className={`h-6 w-6 ${constraint.color} mt-1`} />
                      <div>
                        <h3 className="font-semibold text-secondary-900 mb-1">{constraint.title}</h3>
                        <p className="text-secondary-600 dark:text-secondary-400">{constraint.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 ml-10">
                      {constraint.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-secondary-700">
                          <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Key Features Guide</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Learn about the main features available to you</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {keyFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.title} className="card hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <Icon className={`h-6 w-6 ${feature.color}`} />
                      <h3 className="font-semibold text-secondary-900 dark:text-white">{feature.title}</h3>
                    </div>
                    <p className="text-secondary-600 text-sm">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'dashboard':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Enhanced Dashboard Experience</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Improved organization and user experience with tabbed interface</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card">
                <div className="flex items-center gap-3 mb-4">
                  <Activity className="h-6 w-6 text-primary-600" />
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Tabbed Interface</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Overview Tab</h4>
                      <p className="text-sm text-secondary-600">Summary cards, quick actions, and key metrics</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Tasks Tab</h4>
                      <p className="text-sm text-secondary-600">Comprehensive task management with filtering</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Analytics Tab</h4>
                      <p className="text-sm text-secondary-600">Performance metrics and productivity insights</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Leaves Tab</h4>
                      <p className="text-sm text-secondary-600">Leave balance and management integration</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Calendar Tab</h4>
                      <p className="text-sm text-secondary-600">Outlook integration with conditional rendering</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center gap-3 mb-4">
                  <Minimize2 className="h-6 w-6 text-primary-600" />
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Collapsible Sections</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Recent Activity</h4>
                      <p className="text-sm text-secondary-600">Minimizable activity feed for cleaner interface</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Notes Overview</h4>
                      <p className="text-sm text-secondary-600">Collapsible notes statistics and recent activity</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Time Overview</h4>
                      <p className="text-sm text-secondary-600">Expandable time tracking graphs and metrics</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Past Tasks</h4>
                      <p className="text-sm text-secondary-600">Minimizable completed tasks history</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Upcoming Meetings</h4>
                      <p className="text-sm text-secondary-600">Collapsible calendar events (when Outlook connected)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card bg-blue-50 border-blue-200">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-2">Benefits</h3>
                  <ul className="space-y-1 text-sm text-blue-800">
                    <li>• Cleaner interface with organized tabbed layout</li>
                    <li>• Better navigation with logical grouping of features</li>
                    <li>• Reduced clutter with collapsible sections</li>
                    <li>• Responsive design optimized for all devices</li>
                    <li>• Improved user experience and productivity</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'quick-access':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Global Quick Access Panel</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Floating panel with timer controls and quick actions</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card">
                <div className="flex items-center gap-3 mb-4">
                  <Zap className="h-6 w-6 text-primary-600" />
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Panel Features</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Floating Design</h4>
                      <p className="text-sm text-secondary-600">Fixed bottom-right panel accessible from any page</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Role-Based Visibility</h4>
                      <p className="text-sm text-secondary-600">Only visible to Project Leads and Members (hidden for Admin users)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Real-Time Stats</h4>
                      <p className="text-sm text-secondary-600">Live today's hours and active task count with automatic refresh</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Quick Actions</h4>
                      <p className="text-sm text-secondary-600">One-click access to Log Time, View Tasks, Request Leave, Create Task</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center gap-3 mb-4">
                  <Timer className="h-6 w-6 text-primary-600" />
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Enhanced Timer Management</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Loading States</h4>
                      <p className="text-sm text-secondary-600">"Starting Timer..." and "Stopping Timer..." with spinners</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Contextual Navigation</h4>
                      <p className="text-sm text-secondary-600">View button opens specific task or time entries page</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Visual Feedback</h4>
                      <p className="text-sm text-secondary-600">Color-coded states (green for starting, red for active, orange for stopping)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-medium text-secondary-900">Real-Time Updates</h4>
                      <p className="text-sm text-secondary-600">Automatic stats refresh when timer stops</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card bg-green-50 border-green-200">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-green-900 mb-2">Key Benefits</h3>
                  <ul className="space-y-1 text-sm text-green-800">
                    <li>• Always accessible timer controls from any page</li>
                    <li>• Quick access to frequently used actions</li>
                    <li>• Real-time visibility of work progress</li>
                    <li>• Enhanced user experience with loading states</li>
                    <li>• Automatic data refresh every 5 minutes</li>
                    <li>• Graceful error handling with fallback indicators</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'documentation':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Documentation System</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Create and manage project documentation with rich text editing</p>
            </div>
            
            <div className="space-y-6">
              {/* Access Information */}
              <div className="card bg-blue-50 border-blue-200">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-2">Access Levels</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
                      <div>
                        <strong>Admin & Project Leads:</strong>
                        <ul className="mt-1 space-y-1">
                          <li>• Create new documents</li>
                          <li>• Edit existing documents (only documents they created)</li>
                          <li>• Delete documents (only documents they created)</li>
                          <li>• Upload file attachments</li>
                          <li>• Manage document visibility</li>
                        </ul>
                      </div>
                      <div>
                        <strong>Members:</strong>
                        <ul className="mt-1 space-y-1">
                          <li>• View all project documents</li>
                          <li>• Download documents</li>
                          <li>• Access file attachments</li>
                          <li>• Search and filter documents</li>
                          <li>• No editing permissions</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rich Text Editor Features */}
              <div className="card">
                <h3 className="font-semibold text-secondary-900 dark:text-white mb-4 flex items-center gap-2">
                  <Edit className="h-5 w-5 text-green-600" />
                  Rich Text Editor Features
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div className="space-y-2">
                    <strong className="text-secondary-700 dark:text-secondary-300">Text Formatting:</strong>
                    <ul className="space-y-1 text-secondary-600">
                      <li>• Bold, Italic, Underline</li>
                      <li>• Headings (H1-H4)</li>
                      <li>• Font family & size</li>
                      <li>• Text & background colors</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <strong className="text-secondary-700 dark:text-secondary-300">Structure:</strong>
                    <ul className="space-y-1 text-secondary-600">
                      <li>• Ordered & unordered lists</li>
                      <li>• Text alignment options</li>
                      <li>• Blockquotes</li>
                      <li>• Code blocks</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <strong className="text-secondary-700 dark:text-secondary-300">Advanced:</strong>
                    <ul className="space-y-1 text-secondary-600">
                      <li>• Insert various link types</li>
                      <li>• File attachments</li>
                      <li>• Keyboard shortcuts</li>
                      <li>• Auto-save functionality</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Link Types */}
              <div className="card">
                <h3 className="font-semibold text-secondary-900 dark:text-white mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Link Types Available
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-3">
                    <div>
                      <strong className="text-secondary-700 dark:text-secondary-300">🌐 External Links:</strong>
                      <p className="text-secondary-600 dark:text-secondary-400">Standard web links with custom descriptions</p>
                    </div>
                    <div>
                      <strong className="text-secondary-700 dark:text-secondary-300">⚡ API Documentation:</strong>
                      <p className="text-secondary-600 dark:text-secondary-400">Direct links to API endpoints and docs</p>
                    </div>
                    <div>
                      <strong className="text-secondary-700 dark:text-secondary-300">📖 Swagger/OpenAPI:</strong>
                      <p className="text-secondary-600 dark:text-secondary-400">Interactive API documentation links</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <strong className="text-secondary-700 dark:text-secondary-300">🐙 GitHub Repositories:</strong>
                      <p className="text-secondary-600 dark:text-secondary-400">Direct repository and code links</p>
                    </div>
                    <div>
                      <strong className="text-secondary-700 dark:text-secondary-300">📱 Postman Collections:</strong>
                      <p className="text-secondary-600 dark:text-secondary-400">API testing collection links</p>
                    </div>
                    <div>
                      <strong className="text-secondary-700 dark:text-secondary-300">🚀 Live Demo URLs:</strong>
                      <p className="text-secondary-600 dark:text-secondary-400">Links to deployed applications</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* File Attachments */}
              <div className="card">
                <h3 className="font-semibold text-secondary-900 dark:text-white mb-4 flex items-center gap-2">
                  <Paperclip className="h-5 w-5 text-purple-600" />
                  File Attachments
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="space-y-2">
                    <strong className="text-secondary-700 dark:text-secondary-300">📁 Images:</strong>
                    <ul className="space-y-1 text-secondary-600">
                      <li>• PNG, JPG, GIF, WebP</li>
                      <li>• Max size: 10MB</li>
                      <li>• Inline preview support</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <strong className="text-secondary-700 dark:text-secondary-300">📄 Documents:</strong>
                    <ul className="space-y-1 text-secondary-600">
                      <li>• PDF, DOC, DOCX, TXT</li>
                      <li>• Max size: 25MB</li>
                      <li>• Download functionality</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <strong className="text-secondary-700 dark:text-secondary-300">💻 API Collections:</strong>
                    <ul className="space-y-1 text-secondary-600">
                      <li>• JSON, YAML, YML</li>
                      <li>• Max size: 5MB</li>
                      <li>• Postman collections</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Usage Tips */}
              <div className="card bg-green-50 border-green-200">
                <div className="flex items-start gap-3">
                  <Lightbulb className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-green-900 mb-2">Usage Tips</h3>
                    <ul className="space-y-1 text-sm text-green-800">
                      <li>• Use <kbd className="px-1 py-0.5 bg-green-200 rounded text-xs">Ctrl+S</kbd> to save documents quickly</li>
                      <li>• Press <kbd className="px-1 py-0.5 bg-green-200 rounded text-xs">Escape</kbd> to cancel editing</li>
                      <li>• Documents are automatically linked to the current project</li>
                      <li>• Use tags to organize and categorize documents</li>
                      <li>• Set documents as Public or Private based on team needs</li>
                      <li>• Clean text previews automatically remove Office metadata</li>
                      <li>• All file uploads are stored securely in Azure Blob Storage</li>
                      <li>• Document changes are logged in the activity feed</li>
                      <li>• Use search and filters to quickly find specific documents</li>
                      <li>• Download documents as formatted HTML files with attachments</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'messages':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Platform Messages</h2>
              <p className="text-secondary-600 dark:text-secondary-400">
                Team DMs and group chat — separate from the Pulse AI help assistant
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">Getting started</h3>
                </div>
                <p className="text-blue-800 text-sm leading-relaxed mb-3">
                  Open <strong>Messages</strong> from the sidebar (<code className="text-xs bg-blue-100 px-1 rounded">/messages</code>).
                  Use the compose (pen) icon next to search to start a DM or create a group.
                </p>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Available to all roles: Admin, Project Lead, and Member</li>
                  <li>• Pulse AI answers help/work questions; Messages is person-to-person chat</li>
                  <li>• New messages and invites update in realtime over WebSocket</li>
                </ul>
              </div>

              <div className="bg-white border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="h-5 w-5 text-primary-600" />
                  <h3 className="font-semibold text-secondary-900 dark:text-white">Direct messages</h3>
                </div>
                <ol className="text-sm text-secondary-600 space-y-1 list-decimal list-inside ml-1">
                  <li>Click compose → search for a teammate</li>
                  <li>Send a chat request</li>
                  <li>They Accept or Decline</li>
                  <li>Chat unlocks only after they accept</li>
                </ol>
              </div>

              <div className="bg-white border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-5 w-5 text-indigo-600" />
                  <h3 className="font-semibold text-secondary-900 dark:text-white">Group chats</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-secondary-600">
                  <ul className="space-y-1">
                    <li>• Anyone can create a group</li>
                    <li>• Optional description (up to 500 chars)</li>
                    <li>• Invite any org member</li>
                    <li>• Max <strong>50</strong> participants</li>
                    <li>• Join only after accepting the invite</li>
                  </ul>
                  <ul className="space-y-1">
                    <li>• Leave anytime</li>
                    <li>• Creator deletes only after leaving</li>
                    <li>• Outgoing invites stay on the group row</li>
                    <li>• Pending invites show Accept / Decline</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="h-5 w-5 text-amber-600" />
                  <h3 className="font-semibold text-secondary-900 dark:text-white">Pins, mentions &amp; media</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-secondary-600">
                  <div>
                    <h4 className="font-medium text-secondary-800 mb-2">Pins</h4>
                    <ul className="space-y-1">
                      <li>• Up to 5 pinned chats</li>
                      <li>• Up to 3 pinned messages</li>
                      <li>• Pins appear in the chat header</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-800 mb-2">Mentions</h4>
                    <ul className="space-y-1">
                      <li>• Type <code className="text-xs bg-secondary-100 px-1 rounded">@</code> for users, tasks, projects</li>
                      <li>• Groups: only accepted members</li>
                      <li>• Chips render as @Name</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-800 mb-2">Media</h4>
                    <ul className="space-y-1">
                      <li>• Giphy GIF picker</li>
                      <li>• Image uploads</li>
                      <li>• Voice notes (mic)</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="h-5 w-5 text-amber-600" />
                  <h3 className="font-semibold text-secondary-900 dark:text-white">Tips</h3>
                </div>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Unread badges clear when you open a conversation</li>
                  <li>• If you cannot message someone, they still need to accept your request</li>
                  <li>• If you cannot @mention someone in a group, they have not joined yet</li>
                  <li>• For product help questions, use Pulse AI — not Messages</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 'notes':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Notes System</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Multi-level notes functionality for enhanced project and task collaboration</p>
            </div>
            
            <div className="space-y-6">
              {/* Overview */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <StickyNote className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">Notes Overview</h3>
                </div>
                <p className="text-blue-800 text-sm leading-relaxed">
                  The notes system provides contextual note-taking capabilities across tasks and dashboard, 
                  with privacy controls and team collaboration features. Perfect for tracking progress, 
                  sharing insights, and maintaining project communication.
                </p>
              </div>

              {/* Task-Level Notes */}
              <div className="bg-white border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="h-5 w-5 text-primary-600" />
                  <h3 className="font-semibold text-secondary-900 dark:text-white">Task-Level Notes</h3>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h4 className="font-medium text-secondary-800">✅ Features</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Add notes directly to any task</li>
                        <li>• Private/Public visibility control</li>
                        <li>• Real-time updates across team</li>
                        <li>• Rich text content support</li>
                        <li>• Edit/delete your own notes</li>
                        <li>• UTC+5 time zone display</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium text-secondary-800">🎯 Use Cases</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Progress updates and status</li>
                        <li>• Technical implementation notes</li>
                        <li>• Blockers and dependencies</li>
                        <li>• Meeting outcomes and decisions</li>
                        <li>• Code review feedback</li>
                        <li>• Testing and QA notes</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Privacy Controls */}
              <div className="bg-white border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="h-5 w-5 text-yellow-600" />
                  <h3 className="font-semibold text-secondary-900 dark:text-white">Privacy & Visibility</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-900">Public Notes</span>
                    </div>
                    <ul className="text-sm text-green-800 space-y-1">
                      <li>• Visible to all team members</li>
                      <li>• Perfect for sharing updates</li>
                      <li>• Collaborative discussions</li>
                      <li>• Status announcements</li>
                      <li>• Team coordination</li>
                    </ul>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <EyeOff className="h-4 w-4 text-orange-600" />
                      <span className="font-medium text-orange-900">Private Notes</span>
                    </div>
                    <ul className="text-sm text-orange-800 space-y-1">
                      <li>• Only visible to you</li>
                      <li>• Personal reminders</li>
                      <li>• Internal thoughts</li>
                      <li>• Sensitive information</li>
                      <li>• Draft ideas</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Dashboard Integration */}
              <div className="bg-white border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                  <h3 className="font-semibold text-secondary-900 dark:text-white">Dashboard Integration</h3>
                </div>
                <div className="space-y-3">
                  <div className="bg-purple-50 border border-purple-200 rounded p-3">
                    <h4 className="font-medium text-purple-900 mb-2">Notes Statistics Widget</h4>
                    <ul className="text-sm text-purple-800 space-y-1">
                      <li>• Total notes count across all tasks</li>
                      <li>• Active tasks with notes tracking</li>
                      <li>• Recent task notes preview</li>
                      <li>• Team engagement metrics</li>
                    </ul>
                  </div>
                  <div className="text-sm text-secondary-600">
                    <strong>Quick Access:</strong> Click on any recent note in the dashboard to navigate directly to the related task.
                  </div>
                </div>
              </div>

              {/* How to Use */}
              <div className="bg-white border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="h-5 w-5 text-indigo-600" />
                  <h3 className="font-semibold text-secondary-900 dark:text-white">How to Use Notes</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-secondary-800 mb-2">📝 Adding Notes</h4>
                    <ol className="text-sm text-secondary-600 space-y-1 list-decimal list-inside ml-4">
                      <li>Navigate to any task detail page</li>
                      <li>Click on the "Notes" tab in the sidebar</li>
                      <li>Write your note in the text area</li>
                      <li>Choose visibility: Public (team can see) or Private (only you)</li>
                      <li>Click "Add Note" to save</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-800 mb-2">✏️ Managing Notes</h4>
                    <ul className="text-sm text-secondary-600 space-y-1 list-disc list-inside ml-4">
                      <li>Edit your own notes by clicking the edit icon</li>
                      <li>Toggle visibility between private and public</li>
                      <li>Delete notes with confirmation modal</li>
                      <li>View all notes with author and timestamp</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-800 mb-2">📊 Dashboard Overview</h4>
                    <ul className="text-sm text-secondary-600 space-y-1 list-disc list-inside ml-4">
                      <li>Check the "Notes Overview" widget on your dashboard</li>
                      <li>See total notes and tasks with notes statistics</li>
                      <li>Review recent task notes for quick updates</li>
                      <li>Click on notes to jump to related tasks</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Best Practices */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="h-5 w-5 text-amber-600" />
                  <h3 className="font-semibold text-secondary-900 dark:text-white">Best Practices</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-secondary-800 mb-2">💡 Effective Note-Taking</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Use public notes for team updates</li>
                      <li>• Keep private notes for personal reminders</li>
                      <li>• Update notes regularly with progress</li>
                      <li>• Include relevant context and details</li>
                      <li>• Use clear, concise language</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-800 mb-2">🎯 Team Collaboration</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Share blockers and dependencies</li>
                      <li>• Document decisions and rationale</li>
                      <li>• Provide status updates proactively</li>
                      <li>• Ask questions and seek clarification</li>
                      <li>• Acknowledge team contributions</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Technical Features */}
              <div className="bg-white border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Settings className="h-5 w-5 text-gray-600" />
                  <h3 className="font-semibold text-secondary-900 dark:text-white">Technical Features</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <h4 className="font-medium text-secondary-800 mb-2">🔄 Real-Time</h4>
                    <ul className="text-secondary-600 space-y-1">
                      <li>• Instant synchronization</li>
                      <li>• Live updates across devices</li>
                      <li>• Automatic refresh</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-800 mb-2">🔒 Security</h4>
                    <ul className="text-secondary-600 space-y-1">
                      <li>• Role-based permissions</li>
                      <li>• Input validation</li>
                      <li>• Activity logging</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-800 mb-2">⚡ Performance</h4>
                    <ul className="text-secondary-600 space-y-1">
                      <li>• Loading indicators</li>
                      <li>• Optimized queries</li>
                      <li>• Error handling</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'leave-management':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Leave Management System</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Complete leave tracking with HR policy compliance and approval workflows</p>
            </div>
            
            <div className="space-y-6">
              {/* Overview */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">Leave Management Overview</h3>
                </div>
                <div className="text-sm text-blue-800 space-y-2">
                  <p>• <strong>Total Leave Allocation:</strong> 26 working days per calendar year</p>
                  <p>• <strong>Annual Leave:</strong> 14 days for planned vacations and personal time</p>
                  <p>• <strong>Casual/Medical Leave:</strong> 12 days combined for sick leave and emergencies</p>
                  <p>• <strong>Weekend Exclusion:</strong> Only working days (Monday-Friday) are counted</p>
                  <p>• <strong>Time Restrictions:</strong> Today's leave can only be marked before 10 AM UTC+5 (members requesting for themselves)</p>
                  <p>• <strong>Company Holidays:</strong> Admins set holidays in System Config → Leave Policy; working-day holidays pro-rate weekly hours like leave</p>
                </div>
              </div>

              {/* How to Request Leave */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">How to Request Leave</h3>
                </div>
                <div className="text-sm text-green-800 space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">1.</span>
                    <span>Click the <strong>"Mark Leave"</strong> button in the dashboard header</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">2.</span>
                    <span>Select leave category: <strong>Annual</strong>, <strong>Casual</strong>, or <strong>Medical</strong></span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">3.</span>
                    <span>Enter a description explaining the reason for leave</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">4.</span>
                    <span>Choose date range: <strong>Today</strong>, <strong>Tomorrow</strong>, <strong>This Week</strong>, or <strong>Custom Range</strong></span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">5.</span>
                    <span>Review your leave balance and submit the request</span>
                  </div>
                </div>
              </div>

              {/* Leave Balance */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Gauge className="h-5 w-5 text-purple-600" />
                  <h3 className="font-semibold text-purple-900">Understanding Leave Balance</h3>
                </div>
                <div className="text-sm text-purple-800 space-y-2">
                  <p>• <strong>Dashboard Cards:</strong> View your remaining leave days by category</p>
                  <p>• <strong>Color Indicators:</strong> Green (sufficient), Yellow (low), Red (critical)</p>
                  <p>• <strong>Real-Time Updates:</strong> Balance updates immediately when leave is approved</p>
                  <p>• <strong>Validation:</strong> System prevents requests when insufficient balance remains</p>
                  <p>• <strong>Total Display:</strong> Shows remaining days out of 26 total working days</p>
                </div>
              </div>

              {/* Approval Process */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <User className="h-5 w-5 text-orange-600" />
                  <h3 className="font-semibold text-orange-900">Leave Approval Process</h3>
                </div>
                <div className="text-sm text-orange-800 space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">1.</span>
                    <span>Your leave request is automatically sent to your <strong>Team Lead</strong></span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">2.</span>
                    <span>You receive a confirmation email with team lead details</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">3.</span>
                    <span>Team Lead reviews and approves/declines your request</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">4.</span>
                    <span>You receive notification of the decision via email and in-app notification</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold">5.</span>
                    <span>If approved, your leave balance is updated and status shows in header. Lists show <strong>Approved By</strong> for approved requests</span>
                  </div>
                </div>
              </div>

              {/* Admin Features */}
              {user?.role === 'admin' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="h-5 w-5 text-red-600" />
                    <h3 className="font-semibold text-red-900">Admin Leave Management</h3>
                  </div>
                  <div className="text-sm text-red-800 space-y-2">
                    <p>• <strong>Leave Management Page:</strong> View and manage all leave requests</p>
                    <p>• <strong>Admin Add Leave:</strong> Create leave for any eligible user — auto-approved; same-day allowed after the member cutoff</p>
                    <p>• <strong>Approved By:</strong> Column shows who approved or who created the admin leave</p>
                    <p>• <strong>Approval Actions:</strong> Approve or decline requests with comments</p>
                    <p>• <strong>Revoke:</strong> Undo an approved leave if it was approved by mistake (admin only). The days return to the employee’s balance</p>
                    <p>• <strong>Leave Balance Management:</strong> Edit user leave consumed amounts</p>
                    <p>• <strong>Team Lead Column:</strong> See which team lead each request is intended for</p>
                    <p>• <strong>Company Holidays:</strong> Profile → System Configuration → Leave Policy → add date + name</p>
                    <p>• <strong>Status Tracking:</strong> Monitor pending, approved, and declined requests</p>
                    <p>• <strong>Balance Reports:</strong> Comprehensive leave balance reporting for all users</p>
                  </div>
                </div>
              )}

              {/* Important Notes */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <h3 className="font-semibold text-yellow-900">Important Notes</h3>
                </div>
                <div className="text-sm text-yellow-800 space-y-2">
                  <p>• <strong>Weekend Exclusion:</strong> Saturday and Sunday are not counted in leave calculations</p>
                  <p>• <strong>Time Restrictions:</strong> After 10 AM UTC+5, you can only request leave for tomorrow or future dates</p>
                  <p>• <strong>Conflict Prevention:</strong> System prevents overlapping leave requests</p>
                  <p>• <strong>Balance Validation:</strong> Cannot request leave if insufficient balance remains</p>
                  <p>• <strong>Email Notifications:</strong> All leave events trigger email notifications</p>
                  <p>• <strong>Activity Logging:</strong> All leave operations are logged for audit purposes</p>
                  <p>• <strong>Weekly Hours:</strong> Approved leave and company holidays pro-rate your weekly target (e.g. 1 day off → 32h of 40h)</p>
                </div>
              </div>

              {/* Troubleshooting */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="h-5 w-5 text-gray-600" />
                  <h3 className="font-semibold text-gray-900">Troubleshooting</h3>
                </div>
                <div className="text-sm text-gray-800 space-y-2">
                  <p><strong>Q: Why can't I request leave for today?</strong></p>
                  <p>A: Today's leave can only be requested before 10 AM UTC+5. After that, you can request for tomorrow or future dates. An admin can still add leave for you.</p>
                  
                  <p><strong>Q: Why is my leave request showing "Insufficient balance"?</strong></p>
                  <p>A: You don't have enough remaining days in that specific leave category. Check your balance or contact HR.</p>
                  
                  <p><strong>Q: How do I check my leave balance?</strong></p>
                  <p>A: Your leave balance is displayed on the dashboard cards and in the leave request modal.</p>
                  
                  <p><strong>Q: Who receives my leave request?</strong></p>
                  <p>A: Your leave request is sent to your assigned Team Lead. If no team lead is assigned, it goes to an admin.</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'smart-idle':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Smart Idle Detection Guide</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Understanding how the new Smart Idle Detection system works</p>
            </div>
            
            <div className="space-y-6">
              {/* How It Works */}
              <div className="card">
                <div className="flex items-start gap-4 mb-4">
                  <Gauge className="h-6 w-6 text-indigo-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-secondary-900 mb-1">How Smart Idle Detection Works</h3>
                    <p className="text-secondary-600 dark:text-secondary-400">The timer continues running even when you switch tabs or work in other applications</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">✅ Timer Continues Running</h4>
                    <ul className="space-y-1 text-sm text-green-800">
                      <li>• Working in VS Code</li>
                      <li>• Watching YouTube tutorials</li>
                      <li>• Using other productive apps</li>
                      <li>• Switching between tabs</li>
                      <li>• Minimizing browser</li>
                    </ul>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h4 className="font-medium text-orange-900 mb-2">⚠️ Only Marks Idle When</h4>
                    <ul className="space-y-1 text-sm text-orange-800">
                      <li>• No mouse/keyboard activity</li>
                      <li>• Tab is visible</li>
                      <li>• Browser has focus</li>
                      <li>• After idle threshold (5 min)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Key Benefits */}
              <div className="card">
                <div className="flex items-start gap-4 mb-4">
                  <Lightbulb className="h-6 w-6 text-yellow-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-secondary-900 mb-1">Key Benefits</h3>
                    <p className="text-secondary-600 dark:text-secondary-400">Why this approach is better for productivity tracking</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">🎯 Productivity-First Approach</h4>
                    <p className="text-sm text-blue-800">
                      Recognizes that working in VS Code, watching tutorials, or using other productive applications 
                      is still work time, not idle time. The timer continues running because you're being productive elsewhere.
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">⏱️ Accurate Time Tracking</h4>
                    <p className="text-sm text-green-800">
                      No more false idle detection when you're actually working. Your time is tracked accurately 
                      whether you're in the Time Tracker app or working in other applications.
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-medium text-purple-900 mb-2">🔄 Seamless Workflow</h4>
                    <p className="text-sm text-purple-800">
                      Switch between your development tools, learning resources, and the Time Tracker app 
                      without worrying about losing tracked time. Everything works together seamlessly.
                    </p>
                  </div>
                </div>
              </div>

              {/* Configuration */}
              <div className="card">
                <div className="flex items-start gap-4 mb-4">
                  <Settings className="h-6 w-6 text-gray-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-secondary-900 mb-1">Configuration & Settings</h3>
                    <p className="text-secondary-600 dark:text-secondary-400">Customize idle detection to match your work style</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <h4 className="font-medium text-indigo-900 mb-2">Idle Threshold Options</h4>
                    <ul className="space-y-1 text-sm text-indigo-800">
                      <li>• 3 minutes (Quick detection)</li>
                      <li>• 5 minutes (Default)</li>
                      <li>• 10 minutes (Moderate)</li>
                      <li>• 15 minutes (Relaxed)</li>
                      <li>• 30 minutes (Very relaxed)</li>
                    </ul>
                  </div>
                  <div className="bg-teal-50 p-4 rounded-lg">
                    <h4 className="font-medium text-teal-900 mb-2">Smart Detection Features</h4>
                    <ul className="space-y-1 text-sm text-teal-800">
                      <li>• Tab visibility detection</li>
                      <li>• Window focus monitoring</li>
                      <li>• Activity event tracking</li>
                      <li>• Heartbeat monitoring</li>
                      <li>• Real-time status display</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Best Practices */}
              <div className="card">
                <div className="flex items-start gap-4 mb-4">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-secondary-900 mb-1">Best Practices</h3>
                    <p className="text-secondary-600 dark:text-secondary-400">Tips for maximizing the benefits of Smart Idle Detection</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">✅ Do This</h4>
                    <ul className="space-y-1 text-sm text-green-800">
                      <li>• Keep your development tools open and active</li>
                      <li>• Use the timer when starting work sessions</li>
                      <li>• Switch between productive applications freely</li>
                      <li>• Let the system handle idle detection automatically</li>
                      <li>• Review idle periods to improve productivity</li>
                    </ul>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <h4 className="font-medium text-red-900 mb-2">❌ Avoid This</h4>
                    <ul className="space-y-1 text-sm text-red-800">
                      <li>• Don't manually stop/start timer for every task switch</li>
                      <li>• Don't worry about tab switching affecting time tracking</li>
                      <li>• Don't keep unnecessary tabs open</li>
                      <li>• Don't ignore idle notifications completely</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Troubleshooting */}
              <div className="card">
                <div className="flex items-start gap-4 mb-4">
                  <AlertCircle className="h-6 w-6 text-orange-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-secondary-900 mb-1">Troubleshooting</h3>
                    <p className="text-secondary-600 dark:text-secondary-400">Common questions and solutions</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h4 className="font-medium text-orange-900 mb-2">Q: Why is my timer still running when I switch tabs?</h4>
                    <p className="text-sm text-orange-800">
                      <strong>A:</strong> This is by design! The Smart Idle Detection system continues your timer when you're working 
                      in other applications like VS Code, YouTube, or other productive tools. Only when you're truly idle 
                      (no activity + tab visible) will it mark you as idle.
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Q: How do I know if I'm being marked as idle?</h4>
                    <p className="text-sm text-blue-800">
                      <strong>A:</strong> The system shows real-time status indicators. You'll see visual feedback about 
                      your timer status, tab visibility, and whether you're being tracked as idle. The system also 
                      provides notifications when idle periods are detected.
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">Q: Can I adjust the idle detection sensitivity?</h4>
                    <p className="text-sm text-green-800">
                      <strong>A:</strong> Yes! You can configure the idle threshold from 3 to 30 minutes based on your work style. 
                      Shorter thresholds are better for focused work, while longer thresholds work well for research 
                      and learning activities.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'analytics':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Analytics & Performance Metrics</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Understanding the analytics dashboard and performance tracking</p>
            </div>
            
            <div className="space-y-6">
              {analyticsMetrics.map((metric) => {
                const Icon = metric.icon;
                return (
                  <div key={metric.title} className="card">
                    <div className="flex items-start gap-4 mb-4">
                      <Icon className={`h-6 w-6 ${metric.color} mt-1`} />
                      <div>
                        <h3 className="font-semibold text-secondary-900 mb-1">{metric.title}</h3>
                        <p className="text-secondary-600 dark:text-secondary-400">{metric.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 ml-10">
                      {metric.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-secondary-700">
                          <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'resource-utilization':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Resource Utilization Management</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Comprehensive guide for managing team resources and project assignments</p>
            </div>
            
            <div className="space-y-6">
              {resourceUtilizationGuide.map((guide) => {
                const Icon = guide.icon;
                return (
                  <div key={guide.title} className="card">
                    <div className="flex items-start gap-4 mb-4">
                      <Icon className={`h-6 w-6 ${guide.color} mt-1`} />
                      <div>
                        <h3 className="font-semibold text-secondary-900 mb-1">{guide.title}</h3>
                        <p className="text-secondary-600 dark:text-secondary-400">{guide.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 ml-10">
                      {guide.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-secondary-700">
                          <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'ai-validation':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">AI Validation & Machine Learning</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Intelligent validation for time tracking and task estimation accuracy</p>
            </div>
            
            <div className="space-y-6">
              {aiValidationGuide.map((guide) => {
                const Icon = guide.icon;
                return (
                  <div key={guide.title} className="card">
                    <div className="flex items-start gap-4 mb-4">
                      <Icon className={`h-6 w-6 ${guide.color} mt-1`} />
                      <div>
                        <h3 className="font-semibold text-secondary-900 mb-1">{guide.title}</h3>
                        <p className="text-secondary-600 dark:text-secondary-400">{guide.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 ml-10">
                      {guide.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-secondary-700">
                          <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'github':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">GitHub Integration Guide</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Connect your GitHub account for development time tracking and commit linking</p>
            </div>
            
            <div className="space-y-6">
              {githubIntegrationGuide.map((guide) => {
                const Icon = guide.icon;
                return (
                  <div key={guide.title} className="card">
                    <div className="flex items-start gap-4 mb-4">
                      <Icon className={`h-6 w-6 ${guide.color} mt-1`} />
                      <div>
                        <h3 className="font-semibold text-secondary-900 mb-1">{guide.title}</h3>
                        <p className="text-secondary-600 dark:text-secondary-400">{guide.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 ml-10">
                      {guide.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-secondary-700">
                          <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'github-advanced':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Advanced GitHub Features</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Leverage advanced capabilities for better development tracking and team collaboration</p>
            </div>
            
            <div className="space-y-6">
              {advancedGitHubFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.title} className="card">
                    <div className="flex items-start gap-4 mb-4">
                      <Icon className={`h-6 w-6 ${feature.color} mt-1`} />
                      <div>
                        <h3 className="font-semibold text-secondary-900 mb-1">{feature.title}</h3>
                        <p className="text-secondary-600 dark:text-secondary-400">{feature.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 ml-10">
                      {feature.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-secondary-700">
                          <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'phase4-features':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Advanced Features</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Latest advanced features for enhanced development tracking and team collaboration</p>
            </div>
            
            <div className="space-y-6">
              {phase4Features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.title} className="card">
                    <div className="flex items-start gap-4 mb-4">
                      <Icon className={`h-6 w-6 ${feature.color} mt-1`} />
                      <div>
                        <h3 className="font-semibold text-secondary-900 mb-1">{feature.title}</h3>
                        <p className="text-secondary-600 dark:text-secondary-400">{feature.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 ml-10">
                      {feature.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-secondary-700">
                          <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'calendar':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Microsoft Calendar Integration Guide</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Connect your Outlook calendar for automatic meeting time tracking and event correlation</p>
            </div>
            
            <div className="space-y-6">
              {microsoftCalendarGuide.map((guide) => {
                const Icon = guide.icon;
                return (
                  <div key={guide.title} className="card">
                    <div className="flex items-start gap-4 mb-4">
                      <Icon className={`h-6 w-6 ${guide.color} mt-1`} />
                      <div>
                        <h3 className="font-semibold text-secondary-900 mb-1">{guide.title}</h3>
                        <p className="text-secondary-600 dark:text-secondary-400">{guide.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 ml-10">
                      {guide.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-secondary-700">
                          <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'meeting-time-tracking':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Meeting Time Tracking Guide</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Automatically track time spent in meetings with Microsoft Outlook integration</p>
            </div>
            
            <div className="space-y-6">
              {meetingTimeTrackingGuide.map((guide) => {
                const Icon = guide.icon;
                return (
                  <div key={guide.title} className="card">
                    <div className="flex items-start gap-4 mb-4">
                      <Icon className={`h-6 w-6 ${guide.color} mt-1`} />
                      <div>
                        <h3 className="font-semibold text-secondary-900 mb-1">{guide.title}</h3>
                        <p className="text-secondary-600 dark:text-secondary-400">{guide.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {guide.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-secondary-700">
                          <span className="text-primary-500 mt-1">•</span>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'two-factor':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Two-Factor Authentication Guide</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Secure your account with enhanced authentication</p>
            </div>
            
            <div className="space-y-6">
              {twoFactorAuthGuide.map((guide) => {
                const Icon = guide.icon;
                return (
                  <div key={guide.title} className="card">
                    <div className="flex items-start gap-4 mb-4">
                      <Icon className={`h-6 w-6 ${guide.color} mt-1`} />
                      <div>
                        <h3 className="font-semibold text-secondary-900 mb-1">{guide.title}</h3>
                        <p className="text-secondary-600 dark:text-secondary-400">{guide.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 ml-10">
                      {guide.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-secondary-700">
                          <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'email-notifications':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Automated Email Notifications</h2>
              <p className="text-secondary-600 dark:text-secondary-400">Stay informed with our comprehensive automated email system</p>
            </div>
            
            <div className="space-y-6">
              {/* Schedule Information */}
              <div className="card">
                <div className="flex items-start gap-4 mb-4">
                  <Clock className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-secondary-900 mb-1">Notification Schedule</h3>
                    <p className="text-secondary-600 dark:text-secondary-400">All emails are sent Monday to Friday during business hours (9 AM - 7 PM UTC)</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Daily Notifications</h4>
                    <ul className="space-y-1 text-sm text-blue-800">
                      <li>• 9 AM: Due Today Heads-up</li>
                      <li>• 10 AM: Overdue Task Reminders</li>
                      <li>• 7 PM: Due Today Final Reminder</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">Weekly Notifications</h4>
                    <ul className="space-y-1 text-sm text-green-800">
                      <li>• Member pace reminders (configurable days, e.g. Tue / Thu / Fri)</li>
                      <li>• Team lead under-hours summary (default Monday)</li>
                      <li>• HR full-team weekly report (configurable recipient)</li>
                      <li>• Task weekly summaries (legacy schedule where enabled)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* What Members Receive */}
              <div className="card">
                <div className="flex items-start gap-4 mb-4">
                  <User className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-secondary-900 mb-1">What Members & Project Leads Receive</h3>
                    <p className="text-secondary-600 dark:text-secondary-400">Personal notifications about your own tasks and activities</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">Personal Task Reminders</h4>
                    <ul className="space-y-1 text-sm text-green-800">
                      <li>• Only YOUR overdue tasks (daily at 10 AM)</li>
                      <li>• Only YOUR tasks due today (9 AM & 7 PM)</li>
                      <li>• Only YOUR weekly summary (Monday morning)</li>
                      <li>• Weekly hours pace reminders if you are behind the 40h target</li>
                      <li>• Only YOUR time tracking data</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Privacy & Security</h4>
                    <ul className="space-y-1 text-sm text-blue-800">
                      <li>• Never see other users' tasks or data</li>
                      <li>• Only receive information about your assignments</li>
                      <li>• Personal statistics and progress tracking</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* What Admins Receive */}
              {user?.role === 'admin' && (
                <div className="card">
                  <div className="flex items-start gap-4 mb-4">
                    <Crown className="h-6 w-6 text-yellow-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-secondary-900 mb-1">What Admins Receive</h3>
                      <p className="text-secondary-600 dark:text-secondary-400">Complete system overview and management notifications</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-900 mb-2">System-Wide Overview</h4>
                      <ul className="space-y-1 text-sm text-yellow-800">
                        <li>• Complete team weekly summary (Monday)</li>
                        <li>• All overdue tasks across the system</li>
                        <li>• Total team hours and task statistics</li>
                        <li>• Breakdown by member and project</li>
                      </ul>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h4 className="font-medium text-red-900 mb-2">Escalation Notifications</h4>
                      <ul className="space-y-1 text-sm text-red-800">
                        <li>• Tasks overdue for 3+ days (11 AM daily)</li>
                        <li>• Project-specific escalation alerts</li>
                        <li>• Priority-based task breakdowns</li>
                        <li>• Immediate action required notices</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              

              {/* Email Types */}
              <div className="card">
                <div className="flex items-start gap-4 mb-4">
                  <MessageSquare className="h-6 w-6 text-purple-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-secondary-900 mb-1">Email Types & Content</h3>
                    <p className="text-secondary-600 dark:text-secondary-400">Professional, informative emails with actionable insights</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-3">
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <h4 className="font-medium text-purple-900 mb-1">Overdue Task Reminders</h4>
                      <p className="text-sm text-purple-800">Daily alerts with priority levels, days overdue, and project information</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-1">Due Today Notifications</h4>
                      <p className="text-sm text-blue-800">Morning heads-up and evening reminders with task details and priorities</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-green-50 p-3 rounded-lg">
                      <h4 className="font-medium text-green-900 mb-1">Weekly Hours Enforcement</h4>
                      <p className="text-sm text-green-800">Member pace alerts, team-lead under-hours digests, and optional HR full-team report (see Tracking → Weekly Hours Enforcement)</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <h4 className="font-medium text-green-900 mb-1">Weekly Summaries</h4>
                      <p className="text-sm text-green-800">Comprehensive reports with completed tasks, pending items, and time tracking</p>
                    </div>
                    <div className="bg-red-50 p-3 rounded-lg">
                      <h4 className="font-medium text-red-900 mb-1">Escalation Alerts</h4>
                      <p className="text-sm text-red-800">Priority-based breakdowns with immediate action requirements</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefits */}
              <div className="card">
                <div className="flex items-start gap-4 mb-4">
                  <Zap className="h-6 w-6 text-orange-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-secondary-900 mb-1">Benefits of Automated Notifications</h3>
                    <p className="text-secondary-600 dark:text-secondary-400">Stay on top of your work without manual checking</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <ul className="space-y-2 text-sm text-secondary-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Never miss important deadlines</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Stay informed about your progress</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Professional email templates</span>
                    </li>
                  </ul>
                  <ul className="space-y-2 text-sm text-secondary-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Business hours only (no weekend spam)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Role-based information access</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Actionable insights and next steps</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );


      case 'weekly-hours':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-2">Weekly Hours Enforcement</h2>
              <p className="text-secondary-600 dark:text-secondary-400">
                Stay on track for the weekly hours target (default 40h). Reminders and digests run in app timezone (UTC+5).
              </p>
            </div>

            <div className="card bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <Target className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-1">Goal</h3>
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    Log toward the configured weekly target. If you fall behind expected pace during the week,
                    you get escalating in-app notifications and emails so you can catch up before week end.
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-start gap-4 mb-4">
                <User className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-secondary-900 dark:text-white mb-1">What members receive</h3>
                  <p className="text-secondary-600 dark:text-secondary-400">Pace reminders on configured days (often Tue, Thu, Fri)</p>
                </div>
              </div>
              <ul className="space-y-2 ml-10 text-sm text-secondary-700 dark:text-secondary-300">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                  <span><strong>Gentle → Warning → Final</strong> levels escalate across reminder days if you are still under expected hours</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Email + in-app notification with a link to log hours; Final may also notify your team lead</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Users on leave for the day are skipped</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Admins may limit reminders to selected <strong>departments</strong> (Resource Utilization); none selected = everyone. After selecting departments, uncheck people to exclude them</span>
                </li>
              </ul>
            </div>

            <div className="card">
              <div className="flex items-start gap-4 mb-4">
                <Users className="h-6 w-6 text-primary-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-secondary-900 dark:text-white mb-1">Team lead summary</h3>
                  <p className="text-secondary-600 dark:text-secondary-400">Usually Monday morning (configurable)</p>
                </div>
              </div>
              <ul className="space-y-2 ml-10 text-sm text-secondary-700 dark:text-secondary-300">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Each lead gets one email for <strong>last week</strong> covering reportees under their leave/holiday-adjusted weekly target (approved leave and company holidays pro-rate the 40h; full-week leave is not flagged)</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Grouping uses the Team Lead set on Resource Utilization</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Includes hours logged, gap from target, and timesheet-style breakdown</span>
                </li>
              </ul>
            </div>

            <div className="card">
              <div className="flex items-start gap-4 mb-4">
                <Mail className="h-6 w-6 text-purple-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-secondary-900 dark:text-white mb-1">HR / weekly report recipient</h3>
                  <p className="text-secondary-600 dark:text-secondary-400">One selected user receives a weekly hours digest</p>
                </div>
              </div>
              <ul className="space-y-2 ml-10 text-sm text-secondary-700 dark:text-secondary-300">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Recipient is chosen in System Configuration (any active user — typically HR)</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Day and time are configurable; times use UTC+5</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Admins may limit the digest to selected <strong>departments</strong>; none selected = all departments. Uncheck people in the list to exclude them</span>
                </li>
              </ul>
            </div>

            {user?.role === 'admin' && (
              <>
                <div className="card border-amber-200 bg-amber-50/70 dark:bg-amber-900/20 dark:border-amber-800">
                  <div className="flex items-start gap-4 mb-4">
                    <Settings className="h-6 w-6 text-amber-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-secondary-900 dark:text-white mb-1">Admin configuration</h3>
                      <p className="text-secondary-600 dark:text-secondary-400">Profile → System Configuration → Weekly Hours Enforcement</p>
                    </div>
                  </div>
                  <ul className="space-y-2 ml-10 text-sm text-secondary-700 dark:text-secondary-300">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                      <span>Enable/disable enforcement, pace factor, reminder days/time, escalation order</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                      <span><strong>Departments</strong> for member reminders and HR report (empty = all); company holidays under Leave Policy</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                      <span>Lead summary and HR report day/time; pick HR recipient from users</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                      <span><strong>Dry run</strong>: emails/notifications only to an allowlist; subjects prefixed [DRY RUN] — use before going live</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                      <span><strong>Trigger now</strong> runs each job immediately (bypasses schedule). In dry run, also add member emails if you want their rows in lead reports</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                      <span>Celery dispatcher runs every 5 minutes and enqueues jobs in the matching schedule window</span>
                    </li>
                  </ul>
                </div>

                <div className="card">
                  <div className="flex items-start gap-4 mb-4">
                    <Info className="h-6 w-6 text-secondary-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-secondary-900 dark:text-white mb-1">Related admin tools</h3>
                      <p className="text-secondary-600 dark:text-secondary-400">Analytics Action Alerts and User Detail weekly compliance complement these emails</p>
                    </div>
                  </div>
                  <ul className="space-y-2 ml-10 text-sm text-secondary-700 dark:text-secondary-300">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                      <span>Analytics overview: Action Alerts for under-40h / chronic under-hours</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0" />
                      <span>Office attendance radius is stored in km; the System Config field is labeled in meters (1000 m = 1 km)</span>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={viewMode === 'chat' ? '' : 'space-y-6'}>
      {viewMode === 'chat' ? (
        <HelpChatbot onSwitchToDocs={() => setViewMode('docs')} />
      ) : (
        <div className="px-4 lg:px-8 pt-4 lg:pt-8 pb-8 space-y-6">
        {/* Page Header - only in docs mode */}
        <div className="page-header">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">Help & Documentation</h1>
            <p className="text-secondary-500 dark:text-secondary-400 text-sm mt-0.5">Browse the full documentation</p>
          </div>
          <div className="flex items-center gap-1 p-1 bg-secondary-100 dark:bg-secondary-800 rounded-xl">
            <button
              onClick={() => setViewMode('chat')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400"
            >
              <MessageSquare className="h-4 w-4" />
              Ask AI
            </button>
            <button
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 bg-white dark:bg-secondary-700 text-primary-600 dark:text-primary-400 shadow-sm"
            >
              <BookOpen className="h-4 w-4" />
              Docs
            </button>
          </div>
        </div>
        <div className="flex gap-6">
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-20 space-y-1">
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  value={docSearch}
                  onChange={(e) => setDocSearch(e.target.value)}
                  placeholder="Search docs..."
                  className="input-field w-full !pl-9 !py-2 text-sm"
                />
              </div>

              <nav className="space-y-4 max-h-[calc(100vh-14rem)] overflow-y-auto pr-1">
                {filteredGroups.map((group) => (
                  <div key={group.label}>
                    <p className="text-[11px] font-semibold text-secondary-400 dark:text-secondary-500 uppercase tracking-wider px-3 mb-1.5">
                      {group.label}
                    </p>
                    <div className="space-y-0.5">
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeSection === item.id;
                        return (
                          <button
                            key={item.id}
                            onClick={() => switchSection(item.id)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 relative ${
                              isActive
                                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium'
                                : 'text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 hover:text-secondary-900 dark:hover:text-secondary-200'
                            }`}
                          >
                            {isActive && (
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-500 dark:bg-primary-400 rounded-r" />
                            )}
                            <Icon className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${isActive ? 'scale-110' : ''}`} />
                            <span className="truncate">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </nav>
            </div>
          </aside>

          {/* Mobile Navigation */}
          <div className="lg:hidden w-full">
            <div className="card !p-3 mb-4">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  value={docSearch}
                  onChange={(e) => setDocSearch(e.target.value)}
                  placeholder="Search docs..."
                  className="input-field w-full !pl-9 !py-2 text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {filteredGroups.flatMap(g => g.items).map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => switchSection(item.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                        activeSection === item.id
                          ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400 shadow-sm'
                          : 'bg-secondary-50 dark:bg-secondary-700/50 text-secondary-600 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700'
                      }`}
                    >
                      <Icon className="h-3 w-3" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div key={sectionKey} className="animate-fade-in">
              {renderSection()}
            </div>
          </div>

          {/* Desktop Content */}
          <div className="hidden lg:block flex-1 min-w-0">
            <div key={sectionKey} className="animate-fade-in">
              {renderSection()}
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default HelpGuidelines;